/**
 * Helper to retrieve local system username when running locally.
 */
/**
 * Helper to determine if the code is currently running within a test environment or test runner.
 */
export function isTestEnvironment(): boolean {
  if (typeof process !== "undefined" && process?.env) {
    if (
      process.env.AVATAR_FORCE_NON_TEST === "1" ||
      process.env.AVATAR_FORCE_NON_TEST?.toLowerCase() === "true"
    ) {
      return false;
    }
    if (
      process.env.NODE_ENV === "test" ||
      process.env.BUN_ENV === "test" ||
      process.env.JEST_WORKER_ID !== undefined ||
      process.env.VITEST !== undefined
    ) {
      return true;
    }
  }
  if (typeof globalThis !== "undefined") {
    const g = globalThis as any;
    if (typeof g.describe === "function" && (typeof g.it === "function" || typeof g.test === "function")) {
      return true;
    }
  }
  return false;
}

export function getLocalRunner(): string | undefined {
  if (typeof process !== "undefined" && process?.env) {
    const user =
      process.env.LOCAL_USER ||
      process.env.USER ||
      process.env.USERNAME ||
      process.env.LOGNAME;
    if (user && user.trim()) {
      return user.trim();
    }
  }
  return undefined;
}

/**
 * Checks whether the current backend process is running in a local environment.
 */
export function isLocalEnvironment(): boolean {
  if (typeof process !== "undefined" && process?.env) {
    const envObj = process.env;
    const isLocalVal = envObj.IS_LOCAL?.trim().toLowerCase();
    const localDevVal = envObj.LOCAL_DEV?.trim().toLowerCase();
    const appEnvVal = envObj.APP_ENV?.trim().toLowerCase();
    const environmentVal = envObj.ENVIRONMENT?.trim().toLowerCase();

    // 1. Explicit negative overrides
    const nonLocalEnvs = new Set(["dev", "development", "staging", "prod", "production", "test"]);
    if (
      isLocalVal === "false" ||
      isLocalVal === "0" ||
      localDevVal === "false" ||
      localDevVal === "0" ||
      (appEnvVal && nonLocalEnvs.has(appEnvVal)) ||
      (environmentVal && nonLocalEnvs.has(environmentVal))
    ) {
      return false;
    }

    // 2. Explicit positive local flags
    if (
      isLocalVal === "true" ||
      isLocalVal === "1" ||
      localDevVal === "true" ||
      localDevVal === "1" ||
      appEnvVal === "local" ||
      environmentVal === "local"
    ) {
      return true;
    }

    // 3. CI runner detection (GitHub Actions, GitLab CI, etc.)
    const ciVal = envObj.CI?.trim().toLowerCase();
    if (ciVal === "true" || ciVal === "1") {
      return false;
    }

    // 4. Default heuristic: outside test, cloud runtimes, and production NODE_ENV
    if (
      !isTestEnvironment() &&
      !envObj.AWS_EXECUTION_ENV &&
      !envObj.ECS_CONTAINER_METADATA_URI &&
      !envObj.ECS_CONTAINER_METADATA_URI_V4 &&
      !envObj.LAMBDA_TASK_ROOT &&
      envObj.NODE_ENV?.trim().toLowerCase() !== "production"
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Centralized utility to determine environment ('local' | 'dev' | 'prod') from a hostname or domain string.
 */
export const KNOWN_DEV_HOSTNAMES = new Set([
  "machiavelli.elder-wisdom-dev.innovation.uci.edu",
  "bri.elder-wisdom-dev.innovation.uci.edu",
  "elder-wisdom-dev.innovation.uci.edu",
  "atlas.elder-wisdom-dev.innovation.uci.edu",
  "d3ki2vk2565o1f.cloudfront.net",
  "d1oc4fut3eerl1.cloudfront.net",
  "d86h32kjhatnp.cloudfront.net",
  "d2xapdw27fgcdm.cloudfront.net", // Atlas dev
]);

export const KNOWN_PROD_HOSTNAMES = new Set([
  "machiavelli.elder-wisdom.innovation.uci.edu",
  "bri.elder-wisdom.innovation.uci.edu",
  "elder-wisdom.innovation.uci.edu",
  "d3e29b4kg2715r.cloudfront.net",
  "d2944yen2iny0p.cloudfront.net",
  "dqq3nj4uk5onj.cloudfront.net",
  "devotion.gargantua.llc",
]);

/**
 * Extracts normalized hostname from a host string or full URL.
 */
export function extractHostname(hostOrDomain: string): string {
  const trimmed = hostOrDomain.trim().toLowerCase();
  if (!trimmed) return "";
  try {
    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmed);
    const parsed = new URL(hasScheme ? trimmed : `http://${trimmed}`);
    let hostname = parsed.hostname;
    if (hostname.startsWith("[") && hostname.endsWith("]")) {
      hostname = hostname.slice(1, -1);
    }
    return hostname;
  } catch {
    let unescaped = trimmed.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, "");
    const hostPart = unescaped.split("/")[0]?.split("?")[0]?.split("#")[0] || "";
    if (hostPart.startsWith("[")) {
      return hostPart.replace(/^\[([a-f0-9:]+)\](:\d+)?$/, "$1");
    }
    if (!hostPart.includes("::")) {
      return hostPart.replace(/:\d+$/, "");
    }
    return hostPart;
  }
}

/**
 * Centralized utility to determine environment ('local' | 'dev' | 'prod' | 'unknown') from a hostname or domain string.
 */
export function determineEnvironmentFromDomain(hostOrDomain?: string): string {
  const envObj = typeof process !== "undefined" ? process?.env : undefined;

  // 1. If NO host is provided, infer from ambient process environment variables
  if (!hostOrDomain || typeof hostOrDomain !== "string" || !hostOrDomain.trim()) {
    if (envObj?.APP_ENV) return envObj.APP_ENV.toLowerCase();
    if (envObj?.ENVIRONMENT) return envObj.ENVIRONMENT.toLowerCase();
    if (
      envObj?.IS_LOCAL === "true" ||
      envObj?.IS_LOCAL === "1" ||
      envObj?.LOCAL_DEV === "true" ||
      envObj?.LOCAL_DEV === "1"
    ) {
      return "local";
    }
    if (envObj?.NODE_ENV === "production") return "prod";
    if (envObj?.NODE_ENV === "development") return "dev";
    return "unknown";
  }

  const hostname = extractHostname(hostOrDomain);

  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0" ||
    hostname === "::1" ||
    hostname.endsWith(".local")
  ) {
    return "local";
  }

  // Exact known DEV hostname match
  if (KNOWN_DEV_HOSTNAMES.has(hostname)) {
    return "dev";
  }

  // Exact known PROD hostname match
  if (KNOWN_PROD_HOSTNAMES.has(hostname)) {
    return "prod";
  }

  // DEV substring / suffix heuristics (scoped safely to avoid prefix/subdomain spoofing)
  if (
    (hostname.endsWith(".uci.edu") && hostname.includes("elder-wisdom-dev")) ||
    hostname.startsWith("dev.") ||
    hostname.includes(".dev.") ||
    hostname.includes("-dev.") ||
    hostname.startsWith("staging.") ||
    hostname.includes(".staging.") ||
    hostname.includes("-staging.") ||
    hostname.startsWith("test.") ||
    hostname.includes(".test.") ||
    hostname.includes("-test.")
  ) {
    return "dev";
  }

  // PROD substring / suffix heuristics
  if (
    hostname.endsWith(".elder-wisdom.innovation.uci.edu") ||
    hostname.endsWith(".elder-wisdom.com") ||
    hostname === "elder-wisdom.com"
  ) {
    return "prod";
  }

  // Explicit env overrides when domain pattern is unrecognized
  if (envObj?.APP_ENV) return envObj.APP_ENV.toLowerCase();
  if (envObj?.ENVIRONMENT) return envObj.ENVIRONMENT.toLowerCase();
  if (
    envObj?.IS_LOCAL === "true" ||
    envObj?.IS_LOCAL === "1" ||
    envObj?.LOCAL_DEV === "true" ||
    envObj?.LOCAL_DEV === "1"
  ) {
    return "local";
  }

  // 2. An explicit host was provided but does NOT match any known local/dev/prod pattern
  return "unknown";
}
